import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Wand2, RefreshCw, Sparkles, Loader2, Copy, Check, X,
  UserRound, MapPin, Shield, Package,
} from 'lucide-react';
import {
  generateNames,
  buildAINamePrompt,
  parseAINames,
  guessItemType,
  NAME_CATEGORY_LABEL,
  AI_NAME_SYSTEM_PROMPT,
  SUBTYPE_OPTIONS,
  type NameCategory,
  type CharacterNameOptions,
  type WordNameOptions,
} from '../../utils/nameGenerator';
import { callLLM, serializeUsageMeta } from '../../ai/llmClient';
import './nameGenerator.css';

const CATEGORY_TABS: { id: NameCategory; label: string; icon: React.ReactNode }[] = [
  { id: 'character', label: '人物', icon: <UserRound size={15} /> },
  { id: 'place', label: '地点', icon: <MapPin size={15} /> },
  { id: 'faction', label: '势力', icon: <Shield size={15} /> },
  { id: 'item', label: '物品', icon: <Package size={15} /> },
];

interface NameGeneratorModalProps {
  bookId: string;
  onClose: () => void;
  /** 创建入口：category 已把 place 归一为 faction。调用方应保持本弹窗打开，仅在其上叠加创建弹窗 */
  onCreateEntry: (category: 'character' | 'faction' | 'item', name: string) => void;
  /** 创建弹窗已打开时置灰本弹窗（z-index 让位，保持状态以便返回） */
  paused?: boolean;
}

export function NameGeneratorModal({ bookId, onClose, onCreateEntry, paused = false }: NameGeneratorModalProps) {
  const [category, setCategory] = useState<NameCategory>('character');
  const [region, setRegion] = useState<'east' | 'west'>('east');
  const [gender, setGender] = useState<'male' | 'female' | 'any'>('any');
  const [givenLen, setGivenLen] = useState<1 | 2 | 3>(2);
  const [surname, setSurname] = useState('');
  const [wordLen, setWordLen] = useState<2 | 3 | 4>(3);
  const [subtype, setSubtype] = useState('');
  const [keyword, setKeyword] = useState('');
  const [keywordPos, setKeywordPos] = useState<'' | 'end' | 'middle'>('');
  const [note, setNote] = useState('');

  const [names, setNames] = useState<string[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [model, setModel] = useState('');
  const [provider, setProvider] = useState('deepseek');

  // 读取当前 AI 模型（与 AIAssistantPanel 相同来源：localStorage + 服务端偏好；按供应商 map 记录）
  useEffect(() => {
    const applyPref = (raw: string) => {
      const parsed = JSON.parse(raw) as { modelId?: string; provider?: string; selected?: Record<string, string> };
      if (parsed.selected && typeof parsed.selected === 'object') {
        const prov = parsed.provider || 'deepseek';
        setProvider(prov);
        setModel(parsed.selected[prov] ?? '');
      } else if (parsed.modelId && parsed.provider) {
        setProvider(parsed.provider);
        setModel(parsed.modelId);
      }
    };
    try {
      const localPref = localStorage.getItem('AI_MODEL_PREFERENCE');
      if (localPref) applyPref(localPref);
    } catch { /* ignore */ }
    window.api.getUserPreference('AI_MODEL_PREFERENCE').then((pref) => {
      if (pref?.preference_value) {
        try {
          applyPref(pref.preference_value);
          localStorage.setItem('AI_MODEL_PREFERENCE', pref.preference_value);
        } catch { /* ignore */ }
      }
    }).catch(() => { /* ignore */ });
  }, []);

  const options = useMemo(() => {
    if (category === 'character') {
      const o: CharacterNameOptions = {
        region,
        gender,
        givenNameLength: givenLen,
        surname: surname.trim() || undefined,
      };
      return o;
    }
    const o: WordNameOptions = { length: wordLen, subtype: subtype || undefined, keyword: keyword.trim() || undefined, keywordPosition: keywordPos || undefined };
    return o;
  }, [category, region, gender, givenLen, surname, wordLen, subtype, keyword, keywordPos]);

  // 离线生成：由用户手动触发（点击「生成名字」或输入框回车），避免中文输入法 composition 阶段实时渲染拼音半成品
  const regenerate = useCallback(() => {
    const list = generateNames(category, options, 30);
    setNames(list);
    setSelected(null);
    setCopied(null);
    setConfirmOpen(false);
    setAiError(null);
  }, [category, options]);

  // 选择类条件变化（分类/风格/性别/字数/类型/关键字位置）→ 只清空结果，提示手动生成
  // 注意：依赖数组不含 surname/keyword/note —— 文本输入（含拼音半成品）不触发清空与生成
  useEffect(() => {
    setNames([]);
    setSelected(null);
    setCopied(null);
    setConfirmOpen(false);
    setAiError(null);
  }, [category, region, gender, givenLen, wordLen, subtype, keywordPos]);

  // 切换类型时：清空补充说明与子类筛选，重置选中状态
  const switchCategory = (c: NameCategory) => {
    setCategory(c);
    setNote('');
    setSubtype('');
    setKeyword('');
    setKeywordPos('');
    setSelected(null);
    setCopied(null);
    setConfirmOpen(false);
    setAiError(null);
  };

  const handleAIGenerate = async () => {
    if (!note.trim() || aiLoading) return;
    if (!model) {
      setAiError('未配置 AI 模型，请先在设置中配置');
      return;
    }
    setAiLoading(true);
    setAiError(null);
    try {
      const prompt = buildAINamePrompt(category, options, note);
      const messages = [
        { role: 'system' as const, content: AI_NAME_SYSTEM_PROMPT },
        { role: 'user' as const, content: prompt },
      ];
      const content = await callLLM(model, messages, 0.8, undefined, serializeUsageMeta({ bookId }), provider);
      const parsed = parseAINames(content);
      if (parsed.length === 0) {
        setAiError('AI 未返回有效名字，请调整说明后重试');
      } else {
        setNames(parsed);
        setSelected(null);
        setCopied(null);
        setConfirmOpen(false);
      }
    } catch (err: any) {
      setAiError(err?.message || 'AI 生成失败，请重试');
    } finally {
      setAiLoading(false);
    }
  };

  const handleCopyOnly = async () => {
    if (!selected) return;
    try {
      await navigator.clipboard.writeText(selected);
    } catch { /* 剪贴板不可用时静默 */ }
    setCopied(selected);
    setTimeout(() => setCopied(null), 1600);
  };

  const handleConfirmCreate = () => {
    if (!selected) return;
    setConfirmOpen(false);
    // 保持本弹窗打开（paused 置灰），由父组件叠加创建弹窗
    onCreateEntry(category === 'place' ? 'faction' : category, selected);
  };

  const isCharacter = category === 'character';
  const aiDisabled = !note.trim() || aiLoading;
  const subOptions = !isCharacter ? SUBTYPE_OPTIONS[category as 'place' | 'faction' | 'item'] : [];
  const createLabel = NAME_CATEGORY_LABEL[category === 'place' ? 'faction' : category];

  // Escape：优先关闭选中名字的确认弹窗，其次关闭整个取名工具
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      if (confirmOpen) setConfirmOpen(false);
      else onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [confirmOpen, onClose]);

  return createPortal(
    <div
      className={`ng-overlay${paused ? ' ng-overlay--paused' : ''}`}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="ng-modal">
        {/* 头部 */}
        <div className="ng-header">
          <div className="ng-header-title">
            <Wand2 size={16} />
            <span>取名工具</span>
          </div>
          <button className="ng-close" onClick={onClose} title="关闭 (Esc)">
            <X size={16} />
          </button>
        </div>

        {/* 类型切换 */}
        <div className="ng-tabs">
          {CATEGORY_TABS.map(tab => (
            <button
              key={tab.id}
              className={`ng-tab${category === tab.id ? ' is-active' : ''}`}
              onClick={() => switchCategory(tab.id)}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        <div className="ng-body">
          {/* 左侧：筛选条件 */}
          <div className="ng-filters">
            {isCharacter ? (
              <>
                <div className="ng-field">
                  <div className="ng-field-label">风格</div>
                  <div className="ng-seg">
                    {(['east', 'west'] as const).map(r => (
                      <button
                        key={r}
                        className={`ng-seg-btn${region === r ? ' is-active' : ''}`}
                        onClick={() => setRegion(r)}
                      >
                        {r === 'east' ? '东方' : '西方'}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="ng-field">
                  <div className="ng-field-label">性别</div>
                  <div className="ng-seg">
                    {([['male', '男'], ['female', '女'], ['any', '不限']] as const).map(([v, label]) => (
                      <button
                        key={v}
                        className={`ng-seg-btn${gender === v ? ' is-active' : ''}`}
                        onClick={() => setGender(v)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="ng-field">
                  <div className="ng-field-label">名字字数（不含姓氏）</div>
                  <div className="ng-seg">
                    {([1, 2, 3] as const).map(n => (
                      <button
                        key={n}
                        className={`ng-seg-btn${givenLen === n ? ' is-active' : ''}`}
                        onClick={() => setGivenLen(n)}
                      >
                        {n} 字
                      </button>
                    ))}
                  </div>
                </div>
                <div className="ng-field">
                  <div className="ng-field-label">姓氏（留空随机）</div>
                  <input
                    className="ng-input"
                    value={surname}
                    onChange={e => setSurname(e.target.value)}
                    onKeyDown={e => {
                      // 输入法选词确认后的回车才触发（composition 阶段的回车是候选确认，不生成）
                      if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                        e.preventDefault();
                        regenerate();
                      }
                    }}
                    placeholder={region === 'east' ? '支持单姓与复姓' : '输入西方姓氏，留空随机'}
                    maxLength={4}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="ng-field">
                  <div className="ng-field-label">类型</div>
                  <div className="ng-seg">
                    {[{ id: '', label: '全部' }, ...subOptions].map(s => (
                      <button
                        key={s.id || 'all'}
                        className={`ng-seg-btn${subtype === s.id ? ' is-active' : ''}`}
                        onClick={() => setSubtype(s.id)}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="ng-field">
                  <div className="ng-field-label">名称字数</div>
                  <div className="ng-seg">
                    {([2, 3, 4] as const).map(n => (
                      <button
                        key={n}
                        className={`ng-seg-btn${wordLen === n ? ' is-active' : ''}`}
                        onClick={() => setWordLen(n)}
                      >
                        {n} 字
                      </button>
                    ))}
                  </div>
                </div>
                <div className="ng-field">
                  <div className="ng-field-label">关键词（留空随机）</div>
                  <input
                    className="ng-input"
                    value={keyword}
                    onChange={e => setKeyword(e.target.value)}
                    onKeyDown={e => {
                      // 输入法选词确认后的回车才触发（composition 阶段的回车是候选确认，不生成）
                      if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
                        e.preventDefault();
                        regenerate();
                      }
                    }}
                    placeholder={category === 'item'
                      ? '如：刀、火、雪…生成的名字都包含该词'
                      : '如：幽、天、雪…生成的名字都包含该词'}
                    maxLength={4}
                  />
                </div>
                <div className="ng-field">
                  <div className="ng-field-label">关键字位置</div>
                  <div className="ng-seg">
                    {([['', '不限'], ['end', '末尾'], ['middle', '非末尾']] as const).map(([id, label]) => (
                      <button
                        key={id || 'any'}
                        className={`ng-seg-btn${keywordPos === id ? ' is-active' : ''}`}
                        onClick={() => setKeywordPos(id)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* 生成按钮 + 补充说明：固定弹窗高度下整体推到面板底部 */}
            <div className="ng-generate-section">
              <button className="ng-generate-btn" onClick={regenerate}>
                <RefreshCw size={14} />
                <span>{names.length === 0 ? '生成名字' : '重新生成'}</span>
              </button>
              <div className="ng-field ng-field-note">
              <div className="ng-field-label">补充说明（选填，填写后由 AI 定制生成）</div>
              <textarea
                className="ng-textarea"
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder={isCharacter
                  ? '如：古风仙侠，清冷出尘的医者；或西域来客，粗犷豪迈的刀客…'
                  : '如：沙漠中的绿洲城市；亦正亦邪的江湖组织；能淬炼灵力的丹炉…'}
              />
              <div className="ng-ai-row">
                <button
                  className={`ng-ai-btn${aiDisabled ? ' is-disabled' : ''}`}
                  onClick={() => void handleAIGenerate()}
                  disabled={aiDisabled}
                  title={!model ? '未配置 AI 模型' : ''}
                >
                  {aiLoading ? <Loader2 size={14} className="ng-spin" /> : <Sparkles size={14} />}
                  <span>{aiLoading ? 'AI 生成中…' : 'AI 生成'}</span>
                </button>
                {!note.trim() && <span className="ng-ai-hint">未填写说明时使用本地词库，不消耗 AI</span>}
                {!model && note.trim() && <span className="ng-ai-hint ng-ai-error">未配置 AI 模型</span>}
              </div>
              {aiError && <div className="ng-ai-error">{aiError}</div>}
              </div>
            </div>
          </div>

          {/* 右侧：结果列表 */}
          <div className="ng-results">
            <div className="ng-results-head">
              <span className="ng-results-label">
                {NAME_CATEGORY_LABEL[category]}名字 · {names.length} 个
              </span>
            </div>
            {names.length === 0 ? (
              <div className="ng-empty">调整筛选条件后，点击「生成名字」或按回车键生成</div>
            ) : (
              <div className="ng-name-grid">
                {names.map(name => (
                  <button
                    key={name}
                    className={`ng-name-chip${selected === name ? ' is-selected' : ''}`}
                    onClick={() => { setSelected(name); setCopied(null); setConfirmOpen(true); }}
                    title="点击选择：创建 / 仅复制"
                  >
                    {name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 选中名字后的确认弹窗：创建 / 仅复制 / 取消 */}
      {confirmOpen && selected && (
        <div className="ng-confirm-mask" onMouseDown={(e) => { if (e.target === e.currentTarget) setConfirmOpen(false); }}>
          <div className="ng-confirm">
            <div className="ng-confirm-title">名字「{selected}」</div>
            <div className="ng-confirm-desc">
              {category === 'item' && guessItemType(selected) && (
                <span>将预填类型：{guessItemType(selected)} · </span>
              )}
              创建为{createLabel}，或仅复制到剪贴板
            </div>
            <div className="ng-confirm-actions">
              <button className="ng-btn ng-btn-create" onClick={handleConfirmCreate}>
                <Wand2 size={14} />
                <span>创建{createLabel}</span>
              </button>
              <button className="ng-btn ng-btn-copy" onClick={() => void handleCopyOnly()}>
                {copied === selected ? <Check size={14} /> : <Copy size={14} />}
                <span>{copied === selected ? '已复制' : '仅复制'}</span>
              </button>
              <button className="ng-btn ng-btn-cancel" onClick={() => setConfirmOpen(false)}>
                <X size={14} />
                <span>取消</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}
